import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Download, Users, Eye, 
  Plus, Edit, Trash2, ArrowLeft, BarChart3, TrendingUp, LogOut, Briefcase, X 
} from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '../components/ImageUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [resources, setResources] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  
  // Article form state
  const [articleForm, setArticleForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'SEO & Référencement',
    image: '',
    author: 'Webysee',
    featured: false,
    published: true
  });
  const [editingArticle, setEditingArticle] = useState(null);
  
  // Resource form state
  const [resourceForm, setResourceForm] = useState({
    title: '',
    description: '',
    category: 'Guide SEO',
    file_url: '',
    thumbnail: '',
    file_type: 'PDF'
  });

  // Portfolio form state
  const [portfolioForm, setPortfolioForm] = useState({
    title: '',
    category: 'Site vitrine',
    description: '',
    image_before: '',
    image_after: '',
    image_main: '',
    client_name: '',
    year: new Date().getFullYear().toString(),
    technologies: [],
    results: '',
    website_url: '',
    featured: false
  });
  const [editingProject, setEditingProject] = useState(null);
  const [techInput, setTechInput] = useState('');

  const navigate = useNavigate();

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!checkingAuth && adminUser) {
      fetchData();
    }
  }, [activeTab, checkingAuth, adminUser]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error('Token invalid');
      }

      const data = await res.json();
      setAdminUser(data.admin);
    } catch (error) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin/login');
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    toast.success('Déconnexion réussie');
    navigate('/admin/login');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      // Always fetch stats
      const statsRes = await fetch(`${API_URL}/api/blog/admin/stats`, { headers });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (activeTab === 'articles') {
        const articlesRes = await fetch(`${API_URL}/api/blog/admin/articles`, { headers });
        if (articlesRes.ok) {
          const articlesData = await articlesRes.json();
          setArticles(articlesData.articles || []);
        }
      }

      if (activeTab === 'resources') {
        const resourcesRes = await fetch(`${API_URL}/api/blog/resources`, { headers });
        if (resourcesRes.ok) {
          const resourcesData = await resourcesRes.json();
          setResources(resourcesData || []);
        }
      }

      if (activeTab === 'subscribers') {
        const subscribersRes = await fetch(`${API_URL}/api/blog/admin/subscribers`, { headers });
        if (subscribersRes.ok) {
          const subscribersData = await subscribersRes.json();
          setSubscribers(subscribersData.subscribers || []);
        }
      }

      if (activeTab === 'portfolio') {
        const portfolioRes = await fetch(`${API_URL}/api/portfolio/admin/projects`, { headers });
        if (portfolioRes.ok) {
          const portfolioData = await portfolioRes.json();
          setProjects(portfolioData.projects || []);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleArticleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingArticle 
        ? `${API_URL}/api/blog/admin/articles/${editingArticle.id}`
        : `${API_URL}/api/blog/admin/articles`;
      
      const method = editingArticle ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(articleForm)
      });

      const data = await response.json();
      
      if (!response.ok) {
        // Handle validation errors from Pydantic
        const errorMsg = typeof data.detail === 'string' 
          ? data.detail 
          : Array.isArray(data.detail) 
            ? data.detail.map(e => e.msg).join(', ')
            : 'Erreur de validation';
        toast.error(errorMsg);
        return;
      }

      if (data.success) {
        toast.success(editingArticle ? 'Article mis à jour' : 'Article créé');
        setArticleForm({
          title: '',
          slug: '',
          excerpt: '',
          content: '',
          category: 'SEO & Référencement',
          image: '',
          author: 'Webysee',
          featured: false,
          published: true
        });
        setEditingArticle(null);
        fetchData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Une erreur est survenue');
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/articles/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Article supprimé');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleResourceSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/resources`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(resourceForm)
      });

      const data = await response.json();
      
      if (!response.ok) {
        const errorMsg = typeof data.detail === 'string' 
          ? data.detail 
          : Array.isArray(data.detail) 
            ? data.detail.map(e => e.msg).join(', ')
            : 'Erreur de validation';
        toast.error(errorMsg);
        return;
      }

      if (data.success) {
        toast.success('Ressource créée');
        setResourceForm({
          title: '',
          description: '',
          category: 'Guide SEO',
          file_url: '',
          thumbnail: '',
          file_type: 'PDF'
        });
        fetchData();
      }
    } catch (error) {
      toast.error('Une erreur est survenue');
    }
  };

  const handleDeleteResource = async (id) => {
    if (!window.confirm('Supprimer cette ressource ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/resources/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Ressource supprimée');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteSubscriber = async (id) => {
    if (!window.confirm('Supprimer cet abonné ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/subscribers/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Abonné supprimé');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Portfolio handlers
  const handleAddTechnology = () => {
    if (techInput.trim() && !portfolioForm.technologies.includes(techInput.trim())) {
      setPortfolioForm({
        ...portfolioForm,
        technologies: [...portfolioForm.technologies, techInput.trim()]
      });
      setTechInput('');
    }
  };

  const handleRemoveTechnology = (tech) => {
    setPortfolioForm({
      ...portfolioForm,
      technologies: portfolioForm.technologies.filter(t => t !== tech)
    });
  };

  const handlePortfolioSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingProject 
        ? `${API_URL}/api/portfolio/admin/projects/${editingProject.id}`
        : `${API_URL}/api/portfolio/admin/projects`;
      
      const method = editingProject ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(portfolioForm)
      });

      const data = await response.json();
      
      if (!response.ok) {
        const errorMsg = typeof data.detail === 'string' 
          ? data.detail 
          : Array.isArray(data.detail) 
            ? data.detail.map(e => e.msg).join(', ')
            : 'Erreur de validation';
        toast.error(errorMsg);
        return;
      }

      if (data.success) {
        toast.success(editingProject ? 'Projet mis à jour' : 'Projet créé');
        resetPortfolioForm();
        fetchData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Une erreur est survenue');
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/portfolio/admin/projects/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Projet supprimé');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetPortfolioForm = () => {
    setEditingProject(null);
    setPortfolioForm({
      title: '',
      category: 'Site vitrine',
      description: '',
      image_before: '',
      image_after: '',
      image_main: '',
      client_name: '',
      year: new Date().getFullYear().toString(),
      technologies: [],
      results: '',
      website_url: '',
      featured: false
    });
    setTechInput('');
  };

  const startEditProject = (project) => {
    setEditingProject(project);
    setPortfolioForm({
      title: project.title || '',
      category: project.category || 'Site vitrine',
      description: project.description || '',
      image_before: project.image_before || '',
      image_after: project.image_after || '',
      image_main: project.image_main || '',
      client_name: project.client_name || '',
      year: project.year || new Date().getFullYear().toString(),
      technologies: project.technologies || [],
      results: project.results || '',
      website_url: project.website_url || '',
      featured: project.featured || false
    });
  };

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { id: 'articles', label: 'Articles', icon: FileText },
    { id: 'resources', label: 'Ressources', icon: Download },
    { id: 'portfolio', label: 'Réalisations', icon: Briefcase },
    { id: 'subscribers', label: 'Abonnés', icon: Users },
  ];

  // Show loading while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="bg-black border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-xl font-bold text-white">
              <span style={{ color: '#FF6B00' }}>W</span>ebysee
              <span className="text-accent">.</span>
              <span className="text-white/60 font-normal ml-2">Admin</span>
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {adminUser && (
              <span className="text-white/60 text-sm hidden sm:block">
                {adminUser.name || adminUser.email}
              </span>
            )}
            <Link to="/" className="text-white/60 hover:text-accent text-sm transition-colors">
              Voir le site →
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-white/60 hover:text-red-400 text-sm transition-colors"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <nav className="bg-white/[0.02] border border-white/10 p-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'bg-accent text-black'
                      : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <tab.icon size={18} />
                  {tab.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="w-12 h-12 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Dashboard */}
                {activeTab === 'dashboard' && stats && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-white">Tableau de bord</h2>
                    
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-white/[0.02] border border-white/10 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <FileText className="text-accent" size={24} />
                          <span className="text-green-400 text-xs flex items-center gap-1">
                            <TrendingUp size={12} />
                            {stats.articles?.published || 0} publiés
                          </span>
                        </div>
                        <p className="text-3xl font-bold text-white">{stats.articles?.total || 0}</p>
                        <p className="text-white/50 text-sm">Articles</p>
                      </div>
                      
                      <div className="bg-white/[0.02] border border-white/10 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <Download className="text-accent" size={24} />
                        </div>
                        <p className="text-3xl font-bold text-white">{stats.resources || 0}</p>
                        <p className="text-white/50 text-sm">Ressources</p>
                      </div>
                      
                      <div className="bg-white/[0.02] border border-white/10 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <Users className="text-accent" size={24} />
                        </div>
                        <p className="text-3xl font-bold text-white">{stats.subscribers || 0}</p>
                        <p className="text-white/50 text-sm">Abonnés newsletter</p>
                      </div>
                      
                      <div className="bg-white/[0.02] border border-white/10 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <Eye className="text-accent" size={24} />
                        </div>
                        <p className="text-3xl font-bold text-white">{stats.total_views || 0}</p>
                        <p className="text-white/50 text-sm">Vues totales</p>
                      </div>
                    </div>
                    
                    <div className="bg-white/[0.02] border border-white/10 p-6">
                      <h3 className="text-lg font-semibold text-white mb-4">Téléchargements</h3>
                      <p className="text-4xl font-bold text-accent">{stats.downloads || 0}</p>
                      <p className="text-white/50 text-sm mt-1">ressources téléchargées</p>
                    </div>
                  </div>
                )}

                {/* Articles */}
                {activeTab === 'articles' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold text-white">Articles</h2>
                    </div>
                    
                    {/* Article Form */}
                    <div className="bg-white/[0.02] border border-white/10 p-6">
                      <h3 className="text-lg font-semibold text-white mb-6">
                        {editingArticle ? 'Modifier l\'article' : 'Nouvel article'}
                      </h3>
                      <form onSubmit={handleArticleSubmit} className="space-y-6">
                        {/* Titre et Slug */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Titre de l'article *</label>
                            <input
                              type="text"
                              placeholder="Ex: 10 Techniques SEO pour 2025"
                              value={articleForm.title}
                              onChange={(e) => {
                                setArticleForm({
                                  ...articleForm,
                                  title: e.target.value,
                                  slug: generateSlug(e.target.value)
                                });
                              }}
                              required
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                            />
                          </div>
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Slug (URL) *</label>
                            <input
                              type="text"
                              placeholder="10-techniques-seo-2025"
                              value={articleForm.slug}
                              onChange={(e) => setArticleForm({ ...articleForm, slug: e.target.value })}
                              required
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                            />
                            <p className="text-white/40 text-xs mt-1">URL: /blog/{articleForm.slug || 'slug'}</p>
                          </div>
                        </div>
                        
                        {/* Extrait */}
                        <div>
                          <label className="block text-white/60 text-sm mb-2">Extrait / Description courte *</label>
                          <textarea
                            placeholder="Résumé de l'article qui apparaîtra dans la liste des articles (150-200 caractères recommandés)"
                            value={articleForm.excerpt}
                            onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })}
                            required
                            rows={2}
                            className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                          />
                          <p className="text-white/40 text-xs mt-1">{articleForm.excerpt.length} caractères</p>
                        </div>
                        
                        {/* Contenu */}
                        <div>
                          <label className="block text-white/60 text-sm mb-2">Contenu de l'article * (HTML supporté)</label>
                          <textarea
                            placeholder="<h2>Introduction</h2>
<p>Votre contenu ici...</p>

<h2>Section 1</h2>
<p>Utilisez les balises HTML pour formater:</p>
<ul>
  <li><strong>Gras</strong> pour les mots importants</li>
  <li><em>Italique</em> pour les citations</li>
</ul>"
                            value={articleForm.content}
                            onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                            required
                            rows={12}
                            className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent font-mono text-sm"
                          />
                          <p className="text-white/40 text-xs mt-1">
                            Balises supportées: h2, h3, p, ul, ol, li, strong, em, a, blockquote, code, pre
                          </p>
                        </div>
                        
                        {/* Catégorie et Auteur */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Catégorie *</label>
                            <select
                              value={articleForm.category}
                              onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-accent"
                            >
                              <option value="SEO & Référencement">SEO & Référencement</option>
                              <option value="SEO Technique">SEO Technique</option>
                              <option value="Content Marketing">Content Marketing</option>
                              <option value="Analytics">Analytics</option>
                              <option value="Web Design">Web Design</option>
                              <option value="E-commerce">E-commerce</option>
                              <option value="Actualités">Actualités</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Auteur</label>
                            <input
                              type="text"
                              placeholder="Webysee"
                              value={articleForm.author}
                              onChange={(e) => setArticleForm({ ...articleForm, author: e.target.value })}
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                            />
                          </div>
                        </div>

                        {/* Image de couverture */}
                        <ImageUpload
                          value={articleForm.image}
                          onChange={(url) => setArticleForm({ ...articleForm, image: url })}
                          uploadEndpoint="/api/upload/image/article"
                          label="Image de couverture (1200x630px recommandé)"
                          accept="image/*"
                          maxSize={5}
                        />
                        
                        {/* Options */}
                        <div className="flex flex-wrap items-center gap-6 pt-2 border-t border-white/10">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={articleForm.featured}
                              onChange={(e) => setArticleForm({ ...articleForm, featured: e.target.checked })}
                              className="w-4 h-4 accent-accent"
                            />
                            <span className="text-white/60">⭐ Article à la une</span>
                          </label>
                          
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={articleForm.published}
                              onChange={(e) => setArticleForm({ ...articleForm, published: e.target.checked })}
                              className="w-4 h-4 accent-accent"
                            />
                            <span className="text-white/60">✓ Publié</span>
                          </label>
                        </div>
                        
                        <div className="flex gap-4">
                          <button
                            type="submit"
                            className="px-6 py-3 bg-accent text-black font-medium hover:bg-accent/90 transition-colors"
                          >
                            {editingArticle ? 'Mettre à jour' : 'Créer l\'article'}
                          </button>
                          {editingArticle && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingArticle(null);
                                setArticleForm({
                                  title: '',
                                  slug: '',
                                  excerpt: '',
                                  content: '',
                                  category: 'SEO & Référencement',
                                  image: '',
                                  author: 'Webysee',
                                  featured: false,
                                  published: true
                                });
                              }}
                              className="px-6 py-3 bg-white/5 text-white hover:bg-white/10 transition-colors"
                            >
                              Annuler
                            </button>
                          )}
                        </div>
                      </form>
                    </div>
                    
                    {/* Articles List */}
                    <div className="bg-white/[0.02] border border-white/10">
                      <div className="p-4 border-b border-white/10">
                        <h3 className="font-semibold text-white">Liste des articles ({articles.length})</h3>
                      </div>
                      <div className="divide-y divide-white/10">
                        {articles.map((article) => (
                          <div key={article.id} className="p-4 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3">
                                <h4 className="font-medium text-white truncate">{article.title}</h4>
                                {article.featured && (
                                  <span className="px-2 py-0.5 bg-accent/20 text-accent text-xs">À la une</span>
                                )}
                                {!article.published && (
                                  <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-500 text-xs">Brouillon</span>
                                )}
                              </div>
                              <p className="text-white/50 text-sm mt-1">{article.category} • {article.views || 0} vues</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setEditingArticle(article);
                                  setArticleForm({
                                    title: article.title,
                                    slug: article.slug,
                                    excerpt: article.excerpt,
                                    content: article.content,
                                    category: article.category,
                                    image: article.image || '',
                                    author: article.author,
                                    featured: article.featured,
                                    published: article.published
                                  });
                                }}
                                className="p-2 text-white/60 hover:text-accent transition-colors"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteArticle(article.id)}
                                className="p-2 text-white/60 hover:text-red-400 transition-colors"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                        {articles.length === 0 && (
                          <div className="p-8 text-center text-white/40">
                            Aucun article. Créez votre premier article ci-dessus.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Resources */}
                {activeTab === 'resources' && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-white">Ressources téléchargeables</h2>
                    
                    {/* Resource Form */}
                    <div className="bg-white/[0.02] border border-white/10 p-6">
                      <h3 className="text-lg font-semibold text-white mb-6">Nouvelle ressource</h3>
                      <form onSubmit={handleResourceSubmit} className="space-y-6">
                        {/* Titre et Catégorie */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Titre de la ressource *</label>
                            <input
                              type="text"
                              placeholder="Ex: Guide complet du SEO Local"
                              value={resourceForm.title}
                              onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                              required
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                            />
                          </div>
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Catégorie *</label>
                            <select
                              value={resourceForm.category}
                              onChange={(e) => setResourceForm({ ...resourceForm, category: e.target.value })}
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-accent"
                            >
                              <option value="Guide SEO">Guide SEO</option>
                              <option value="Checklist">Checklist</option>
                              <option value="Template">Template</option>
                              <option value="Ebook">Ebook</option>
                              <option value="Infographie">Infographie</option>
                              <option value="Cas d'étude">Cas d'étude</option>
                            </select>
                          </div>
                        </div>
                        
                        {/* Description */}
                        <div>
                          <label className="block text-white/60 text-sm mb-2">Description *</label>
                          <textarea
                            placeholder="Décrivez ce que contient cette ressource et pourquoi elle est utile..."
                            value={resourceForm.description}
                            onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                            required
                            rows={3}
                            className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                          />
                          <p className="text-white/40 text-xs mt-1">{resourceForm.description.length} caractères</p>
                        </div>
                        
                        {/* Fichier à télécharger */}
                        <ImageUpload
                          value={resourceForm.file_url}
                          onChange={(url) => setResourceForm({ ...resourceForm, file_url: url })}
                          uploadEndpoint="/api/upload/resource"
                          label="Fichier à télécharger * (PDF, XLSX, DOCX, PPTX, ZIP - Max 50MB)"
                          accept=".pdf,.xlsx,.docx,.pptx,.zip,.png,.jpg,.jpeg"
                          maxSize={50}
                          previewHeight="h-20"
                        />

                        {/* Type de fichier */}
                        <div>
                          <label className="block text-white/60 text-sm mb-2">Type de fichier (pour l'affichage)</label>
                          <select
                            value={resourceForm.file_type}
                            onChange={(e) => setResourceForm({ ...resourceForm, file_type: e.target.value })}
                            className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-accent"
                          >
                            <option value="PDF">📄 PDF</option>
                            <option value="XLSX">📊 Excel (XLSX)</option>
                            <option value="DOCX">📝 Word (DOCX)</option>
                            <option value="PPTX">📽️ PowerPoint (PPTX)</option>
                            <option value="ZIP">📦 Archive (ZIP)</option>
                            <option value="PNG">🖼️ Image (PNG)</option>
                          </select>
                        </div>

                        {/* Miniature */}
                        <ImageUpload
                          value={resourceForm.thumbnail}
                          onChange={(url) => setResourceForm({ ...resourceForm, thumbnail: url })}
                          uploadEndpoint="/api/upload/image/thumbnail"
                          label="Miniature (optionnel)"
                          accept="image/*"
                          maxSize={5}
                          previewHeight="h-32"
                        />
                        
                        <button
                          type="submit"
                          className="px-6 py-3 bg-accent text-black font-medium hover:bg-accent/90 transition-colors"
                        >
                          Ajouter la ressource
                        </button>
                      </form>
                    </div>
                    
                    {/* Resources List */}
                    <div className="bg-white/[0.02] border border-white/10">
                      <div className="p-4 border-b border-white/10">
                        <h3 className="font-semibold text-white">Ressources disponibles ({resources.length})</h3>
                      </div>
                      <div className="divide-y divide-white/10">
                        {resources.map((resource) => (
                          <div key={resource.id} className="p-4 flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-white">{resource.title}</h4>
                              <p className="text-white/50 text-sm">{resource.category} • {resource.file_type} • {resource.downloads || 0} téléchargements</p>
                            </div>
                            <button
                              onClick={() => handleDeleteResource(resource.id)}
                              className="p-2 text-white/60 hover:text-red-400 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                        {resources.length === 0 && (
                          <div className="p-8 text-center text-white/40">
                            Aucune ressource. Ajoutez votre première ressource ci-dessus.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Subscribers */}
                {activeTab === 'subscribers' && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-white">Abonnés Newsletter</h2>
                    
                    <div className="bg-white/[0.02] border border-white/10">
                      <div className="p-4 border-b border-white/10">
                        <h3 className="font-semibold text-white">Liste des abonnés ({subscribers.length})</h3>
                      </div>
                      <div className="divide-y divide-white/10">
                        {subscribers.map((subscriber) => (
                          <div key={subscriber.id} className="p-4 flex items-center justify-between">
                            <div>
                              <p className="font-medium text-white">{subscriber.email}</p>
                              <p className="text-white/50 text-sm">
                                Inscrit le {new Date(subscriber.subscribed_at).toLocaleDateString('fr-FR')} • 
                                Source: {subscriber.source}
                              </p>
                            </div>
                            <button
                              onClick={() => handleDeleteSubscriber(subscriber.id)}
                              className="p-2 text-white/60 hover:text-red-400 transition-colors"
                              title="Supprimer l'abonné"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                        {subscribers.length === 0 && (
                          <div className="p-8 text-center text-white/40">
                            Aucun abonné pour le moment.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Portfolio / Réalisations */}
                {activeTab === 'portfolio' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold text-white">Réalisations / Portfolio</h2>
                    </div>
                    
                    {/* Portfolio Form */}
                    <div className="bg-white/[0.02] border border-white/10 p-6">
                      <h3 className="text-lg font-semibold text-white mb-6">
                        {editingProject ? 'Modifier le projet' : 'Nouveau projet'}
                      </h3>
                      <form onSubmit={handlePortfolioSubmit} className="space-y-6">
                        {/* Titre et Catégorie */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Titre du projet *</label>
                            <input
                              type="text"
                              placeholder="Ex: Refonte e-commerce Boutique Mode"
                              value={portfolioForm.title}
                              onChange={(e) => setPortfolioForm({ ...portfolioForm, title: e.target.value })}
                              required
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                              data-testid="portfolio-title-input"
                            />
                          </div>
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Catégorie *</label>
                            <select
                              value={portfolioForm.category}
                              onChange={(e) => setPortfolioForm({ ...portfolioForm, category: e.target.value })}
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-accent"
                              data-testid="portfolio-category-select"
                            >
                              <option value="Site vitrine">Site vitrine</option>
                              <option value="E-commerce">E-commerce</option>
                              <option value="Application web">Application web</option>
                              <option value="Refonte">Refonte</option>
                              <option value="Landing page">Landing page</option>
                              <option value="SEO">SEO</option>
                            </select>
                          </div>
                        </div>

                        {/* Description */}
                        <div>
                          <label className="block text-white/60 text-sm mb-2">Description du projet *</label>
                          <textarea
                            placeholder="Décrivez le projet, le contexte, les objectifs et les solutions apportées..."
                            value={portfolioForm.description}
                            onChange={(e) => setPortfolioForm({ ...portfolioForm, description: e.target.value })}
                            required
                            rows={4}
                            className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                            data-testid="portfolio-description-input"
                          />
                        </div>

                        {/* Client et Année */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Nom du client</label>
                            <input
                              type="text"
                              placeholder="Ex: Boutique Élégance"
                              value={portfolioForm.client_name}
                              onChange={(e) => setPortfolioForm({ ...portfolioForm, client_name: e.target.value })}
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                              data-testid="portfolio-client-input"
                            />
                          </div>
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Année</label>
                            <input
                              type="text"
                              placeholder="2025"
                              value={portfolioForm.year}
                              onChange={(e) => setPortfolioForm({ ...portfolioForm, year: e.target.value })}
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                              data-testid="portfolio-year-input"
                            />
                          </div>
                        </div>

                        {/* Technologies */}
                        <div>
                          <label className="block text-white/60 text-sm mb-2">Technologies utilisées</label>
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              placeholder="Ex: React, Node.js, MongoDB..."
                              value={techInput}
                              onChange={(e) => setTechInput(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTechnology())}
                              className="flex-1 bg-white/5 border border-white/10 px-4 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                              data-testid="portfolio-tech-input"
                            />
                            <button
                              type="button"
                              onClick={handleAddTechnology}
                              className="px-4 py-2 bg-accent text-black font-medium hover:bg-accent/90 transition-colors"
                              data-testid="portfolio-add-tech-btn"
                            >
                              Ajouter
                            </button>
                          </div>
                          {portfolioForm.technologies.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {portfolioForm.technologies.map((tech, idx) => (
                                <span 
                                  key={idx} 
                                  className="inline-flex items-center gap-1 px-3 py-1 bg-accent/20 text-accent text-sm"
                                >
                                  {tech}
                                  <button 
                                    type="button" 
                                    onClick={() => handleRemoveTechnology(tech)}
                                    className="hover:text-white ml-1"
                                  >
                                    <X size={14} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Résultats et URL */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white/60 text-sm mb-2">Résultats obtenus</label>
                            <input
                              type="text"
                              placeholder="Ex: +200% de trafic, +50% de conversions"
                              value={portfolioForm.results}
                              onChange={(e) => setPortfolioForm({ ...portfolioForm, results: e.target.value })}
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                              data-testid="portfolio-results-input"
                            />
                          </div>
                          <div>
                            <label className="block text-white/60 text-sm mb-2">URL du site (si en ligne)</label>
                            <input
                              type="url"
                              placeholder="https://www.example.com"
                              value={portfolioForm.website_url}
                              onChange={(e) => setPortfolioForm({ ...portfolioForm, website_url: e.target.value })}
                              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent"
                              data-testid="portfolio-url-input"
                            />
                          </div>
                        </div>

                        {/* Images Section */}
                        <div className="border-t border-white/10 pt-6">
                          <h4 className="text-white font-medium mb-4">Images du projet</h4>
                          <p className="text-white/40 text-sm mb-4">
                            Vous pouvez soit ajouter une image principale, soit des images avant/après pour montrer la transformation.
                          </p>
                          
                          <div className="space-y-6">
                            {/* Image principale */}
                            <ImageUpload
                              value={portfolioForm.image_main}
                              onChange={(url) => setPortfolioForm({ ...portfolioForm, image_main: url })}
                              uploadEndpoint="/api/upload/image/portfolio"
                              label="Image principale (1200x800px recommandé)"
                              accept="image/*"
                              maxSize={5}
                            />

                            <div className="relative">
                              <div className="absolute inset-x-0 top-1/2 border-t border-white/10"></div>
                              <p className="relative text-center text-white/40 text-sm">
                                <span className="bg-[#0a0a0a] px-4">OU images Avant/Après</span>
                              </p>
                            </div>

                            {/* Images Avant/Après */}
                            <div className="grid md:grid-cols-2 gap-4">
                              <ImageUpload
                                value={portfolioForm.image_before}
                                onChange={(url) => setPortfolioForm({ ...portfolioForm, image_before: url })}
                                uploadEndpoint="/api/upload/image/portfolio"
                                label="Image AVANT"
                                accept="image/*"
                                maxSize={5}
                                previewHeight="h-32"
                              />
                              <ImageUpload
                                value={portfolioForm.image_after}
                                onChange={(url) => setPortfolioForm({ ...portfolioForm, image_after: url })}
                                uploadEndpoint="/api/upload/image/portfolio"
                                label="Image APRÈS"
                                accept="image/*"
                                maxSize={5}
                                previewHeight="h-32"
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Options */}
                        <div className="flex items-center gap-6 pt-4 border-t border-white/10">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={portfolioForm.featured}
                              onChange={(e) => setPortfolioForm({ ...portfolioForm, featured: e.target.checked })}
                              className="w-4 h-4 accent-accent"
                              data-testid="portfolio-featured-checkbox"
                            />
                            <span className="text-white/60">⭐ Projet à la une (affiché en premier)</span>
                          </label>
                        </div>
                        
                        <div className="flex gap-4">
                          <button
                            type="submit"
                            className="px-6 py-3 bg-accent text-black font-medium hover:bg-accent/90 transition-colors"
                            data-testid="portfolio-submit-btn"
                          >
                            {editingProject ? 'Mettre à jour' : 'Créer le projet'}
                          </button>
                          {editingProject && (
                            <button
                              type="button"
                              onClick={resetPortfolioForm}
                              className="px-6 py-3 bg-white/5 text-white hover:bg-white/10 transition-colors"
                              data-testid="portfolio-cancel-btn"
                            >
                              Annuler
                            </button>
                          )}
                        </div>
                      </form>
                    </div>
                    
                    {/* Projects List */}
                    <div className="bg-white/[0.02] border border-white/10">
                      <div className="p-4 border-b border-white/10">
                        <h3 className="font-semibold text-white">Projets existants ({projects.length})</h3>
                      </div>
                      <div className="divide-y divide-white/10">
                        {projects.map((project) => (
                          <div key={project.id} className="p-4 flex items-center justify-between" data-testid={`portfolio-item-${project.id}`}>
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              {/* Thumbnail */}
                              {(project.image_main || project.image_after) && (
                                <img 
                                  src={project.image_main || project.image_after} 
                                  alt={project.title}
                                  className="w-16 h-12 object-cover border border-white/10 flex-shrink-0"
                                />
                              )}
                              {!project.image_main && !project.image_after && (
                                <div className="w-16 h-12 bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                                  <Briefcase size={20} className="text-white/20" />
                                </div>
                              )}
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                  <h4 className="font-medium text-white truncate">{project.title}</h4>
                                  {project.featured && (
                                    <span className="px-2 py-0.5 bg-accent/20 text-accent text-xs flex-shrink-0">À la une</span>
                                  )}
                                </div>
                                <p className="text-white/50 text-sm mt-1">
                                  {project.category} 
                                  {project.client_name && ` • ${project.client_name}`}
                                  {project.year && ` • ${project.year}`}
                                </p>
                                {project.technologies?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {project.technologies.slice(0, 3).map((tech, idx) => (
                                      <span key={idx} className="text-xs text-white/40">{tech}{idx < Math.min(2, project.technologies.length - 1) ? ',' : ''}</span>
                                    ))}
                                    {project.technologies.length > 3 && (
                                      <span className="text-xs text-white/40">+{project.technologies.length - 3}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {project.website_url && (
                                <a
                                  href={project.website_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 text-white/60 hover:text-accent transition-colors"
                                  title="Voir le site"
                                >
                                  <Eye size={16} />
                                </a>
                              )}
                              <button
                                onClick={() => startEditProject(project)}
                                className="p-2 text-white/60 hover:text-accent transition-colors"
                                data-testid={`portfolio-edit-${project.id}`}
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteProject(project.id)}
                                className="p-2 text-white/60 hover:text-red-400 transition-colors"
                                data-testid={`portfolio-delete-${project.id}`}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                        {projects.length === 0 && (
                          <div className="p-8 text-center text-white/40">
                            Aucun projet. Ajoutez votre première réalisation ci-dessus.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
